import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, TrendingUp, Trophy, Sparkles, ShieldCheck } from "lucide-react";
import { clubInfo, MbtiBadge } from "@/lib/mbti";
import { Avatar } from "@/components/PhotoUpload";

function GoldenPadrinhoPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/recruit-bonus/pending").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (row) => {
    try {
      await api.put(`/players/${row.player_id}/recruit-bonus`, { approved: !row.approved });
      toast.success(!row.approved
        ? `Padrinho Dourado autorizado para ${row.name} (+50 pts)`
        : `Autorização retirada a ${row.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (user?.role !== "admin" || !data || data.items.length === 0) return null;

  return (
    <div className="kw-card p-6" data-testid="golden-padrinho-panel">
      <div className="flex items-center gap-3 mb-4 border-b border-[#222] pb-4">
        <ShieldCheck className="w-6 h-6 text-[#EAB308]" />
        <div>
          <div className="kw-overline">Autorização do admin · {data.pending} a aguardar</div>
          <h2 className="font-display text-2xl font-black uppercase">Padrinho Dourado</h2>
        </div>
      </div>
      <p className="text-xs text-[#737373] mb-4">
        Quem recomenda consultores que entram no Growth League e reportam fica elegível aos +50 pts.
        Os pontos só entram na tua autorização explícita.
      </p>
      <div className="space-y-2">
        {data.items.map((row) => (
          <div key={row.player_id} className="flex items-center justify-between gap-4 border border-[#222] px-4 py-3"
            data-testid={`padrinho-row-${row.player_id}`}>
            <div>
              <div className="font-semibold text-sm">{row.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                {row.club || "sem clube"} · {row.recruits_count} afilhados activos
              </div>
            </div>
            <button onClick={() => toggle(row)}
              className={row.approved ? "kw-btn-secondary !py-2 !px-4 text-[11px]" : "kw-btn-primary !py-2 !px-4 text-[11px]"}
              data-testid={`padrinho-toggle-${row.player_id}`}>
              {row.approved ? "Retirar autorização" : "Autorizar +50 pts"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Recruitment() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/recruitment").then((r) => setData(r.data)).catch(() => setData({ per_player: [], per_club: [], totals: {} })); }, []);

  if (!data) return <div className="kw-overline">A carregar…</div>;
  const { per_player, per_club, totals } = data;
  const top = per_player[0];

  return (
    <div className="space-y-8 kw-fade-up" data-testid="recruitment-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kw-overline mb-2">Crescimento</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Atividade de <span className="text-[#E50000]">Recrutamento</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm max-w-2xl">
            Acompanha quem está a trazer mais consultores — externos para a KW Lead e internos para o jogo.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={UserPlus} label="GS Externo (KW Lead)" value={totals.gs_external_total || 0} accent="#E50000" />
        <Kpi icon={Sparkles} label="GS Interno (Jogo)" value={totals.gs_internal_total || 0} accent="#22C55E" />
        <Kpi icon={Trophy} label="Recrutadores Ativos" value={totals.active_recruiters || 0} />
        <Kpi icon={TrendingUp} label="Top recrutador" value={top ? top.name : "—"} subtitle={top ? `${top.total} novos` : ""} small />
      </div>

      <GoldenPadrinhoPanel />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 kw-card overflow-x-auto" data-testid="recruitment-players">
          <div className="p-5 border-b border-[#222] flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#E50000]" />
            <h2 className="font-display text-2xl font-black uppercase">Connectors (ranking)</h2>
          </div>
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#222]">
              <tr className="kw-overline">
                <th className="p-3 w-12">#</th>
                <th className="p-3 w-12"></th>
                <th className="p-3">Consultor</th>
                <th className="p-3">Clube</th>
                <th className="p-3 text-right">GS ext.</th>
                <th className="p-3 text-right">GS int.</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Pontos</th>
              </tr>
            </thead>
            <tbody>
              {per_player.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-[#737373]">Ainda ninguém recrutou nesta edição.</td></tr>
              )}
              {per_player.map((p, i) => (
                <tr key={p.player_id} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                  <td className="p-3 font-display text-2xl font-black">
                    <span className={i === 0 ? "text-[#E50000]" : ""}>{String(i + 1).padStart(2, "0")}</span>
                  </td>
                  <td className="p-3"><Avatar photoUrl={p.photo_url} name={p.name} size={36} /></td>
                  <td className="p-3 font-semibold">{p.name}<div className="mt-1"><MbtiBadge mbti={p.mbti} compact /></div></td>
                  <td className="p-3">{p.club ? <MbtiBadge club={p.club} /> : "—"}</td>
                  <td className="p-3 text-right text-[#E50000] font-display text-lg font-black">{p.gs_external}</td>
                  <td className="p-3 text-right text-[#22C55E] font-display text-lg font-black">{p.gs_internal}</td>
                  <td className="p-3 text-right font-bold">{p.total}</td>
                  <td className="p-3 text-right font-display text-xl font-black">{p.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="kw-card p-5" data-testid="recruitment-clubs">
          <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
            <Sparkles className="w-5 h-5 text-[#E50000]" />
            <h2 className="font-display text-2xl font-black uppercase">Por clube</h2>
          </div>
          <div className="space-y-3">
            {per_club.length === 0 && <p className="text-[#737373] text-sm">Sem atividade ainda.</p>}
            {per_club.map((c) => {
              const info = clubInfo(c.club);
              return (
                <div key={c.club} className="border p-3" style={{ borderColor: info?.border, background: info?.bg }}>
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg font-black uppercase" style={{ color: info?.color }}>{c.club}</div>
                    <div className="font-display text-2xl font-black">{c.total}</div>
                  </div>
                  <div className="flex gap-3 mt-1 text-xs text-[#A3A3A3]">
                    <span><strong className="text-[#E50000]">{c.gs_external}</strong> ext.</span>
                    <span><strong className="text-[#22C55E]">{c.gs_internal}</strong> int.</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, subtitle, small }) {
  return (
    <div className="kw-card p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5" style={{ color: accent || "#A3A3A3" }} />
        <div className="kw-overline">{label}</div>
      </div>
      <div className={`font-display font-black ${small ? "text-2xl" : "text-4xl"}`} style={{ color: accent || "#fff" }}>
        {value}
      </div>
      {subtitle && <div className="kw-overline mt-1 text-[#737373]">{subtitle}</div>}
    </div>
  );
}
